import type { HTMLAttributes } from "react";
import { cn } from "../../utils/cn";

type BadgeVariant =
  | "default"
  | "muted"
  | "primary"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "outline";

type BadgeSize = "xs" | "sm" | "md";

type BadgeProps = HTMLAttributes<HTMLSpanElement> & {
  variant?: BadgeVariant;
  size?: BadgeSize;
};

const variantClasses: Record<BadgeVariant, string> = {
  default: "bg-muted/50 text-foreground border-border/50",
  muted: "bg-muted/40 text-muted-foreground border-border/40",
  primary: "bg-primary/15 text-primary border-primary/30",
  info: "bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30",
  success: "bg-emerald-500/15 text-emerald-500 border-emerald-500/30",
  warning: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  danger: "bg-red-500/15 text-red-500 border-red-500/30",
  outline: "bg-transparent text-foreground border-border/50"
};

const sizeClasses: Record<BadgeSize, string> = {
  xs: "px-1.5 py-[1px] text-[10px] leading-tight",
  sm: "px-2 py-0.5 text-xs",
  md: "px-2.5 py-1 text-xs"
};

export const Badge = ({
  variant = "default",
  size = "sm",
  className,
  ...props
}: BadgeProps) => (
  <span
    className={cn(
      "inline-flex items-center gap-1 rounded-md border font-medium",
      variantClasses[variant],
      sizeClasses[size],
      className
    )}
    {...props}
  />
);
